import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  MessageSquare, Send, ArrowLeft, User, Clock, CheckCheck, Mail
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StudentMessages = () => {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/student/login');
    } else if (user && user.role !== 'student') {
      navigate('/');
    } else if (user) {
      fetchMessages();
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (messageId && messages.length > 0) {
      const msg = messages.find(m => m.id === messageId);
      if (msg) {
        viewMessage(msg);
      }
    }
  }, [messageId, messages]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/student/messages`, { withCredentials: true });
      setMessages(res.data.messages);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/student/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const viewMessage = async (message) => {
    try {
      const res = await axios.get(`${API}/student/messages/${message.id}`, { withCredentials: true });
      setSelectedMessage(res.data);
      // Update unread count
      fetchMessages();
    } catch (error) {
      toast.error('Failed to load message');
    }
  };

  const sendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedMessage) return;

    try {
      await axios.post(`${API}/student/messages/${selectedMessage.id}/reply`, {
        message: replyText
      }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyText('');
      viewMessage(selectedMessage); // Refresh message thread
    } catch (error) {
      toast.error('Failed to send reply');
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  if (authLoading || loading) {
    return (
      <div className="pt-20 min-h-screen bg-[#060F22] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00AEEF]"></div>
      </div>
    );
  }

  return (
    <div className="pt-20 min-h-screen bg-[#060F22]">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/student/dashboard')}
              className="p-2 text-[#94A3B8] hover:text-white rounded-lg hover:bg-[#0A1F44]"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <MessageSquare className="w-7 h-7 text-[#00AEEF]" />
                Messages
              </h1>
              {unreadCount > 0 && (
                <p className="text-[#00FFE0] text-sm">{unreadCount} unread message(s)</p>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden">
            <div className="p-4 border-b border-[#00AEEF]/10">
              <h2 className="text-white font-medium">Conversations</h2>
            </div>
            <div className="max-h-[600px] overflow-y-auto">
              {messages.length === 0 ? (
                <div className="p-8 text-center">
                  <Mail className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">No messages yet</p>
                  <p className="text-[#94A3B8] text-sm">Your teachers will contact you here</p>
                </div>
              ) : (
                messages.map((msg) => {
                  const hasUnread = msg.messages?.some(m => m.sender === 'teacher' && !m.read);
                  return (
                    <div
                      key={msg.id}
                      onClick={() => viewMessage(msg)}
                      className={`p-4 border-b border-[#00AEEF]/10 cursor-pointer transition-all ${
                        selectedMessage?.id === msg.id 
                          ? 'bg-[#00AEEF]/10 border-l-2 border-l-[#00AEEF]' 
                          : 'hover:bg-[#060F22]/50'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">{msg.teacher_name}</p>
                            {hasUnread && (
                              <span className="w-2 h-2 bg-[#00FFE0] rounded-full"></span>
                            )}
                          </div>
                          <p className="text-[#00AEEF] text-sm">{msg.subject}</p>
                          <p className="text-[#94A3B8] text-xs mt-1 line-clamp-1">
                            {msg.messages?.[msg.messages.length - 1]?.content}
                          </p>
                        </div>
                        <span className="text-[#94A3B8] text-xs">
                          {new Date(msg.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Message Thread */}
          <div className="lg:col-span-2 bg-[#0A1F44]/50 border border-[#00AEEF]/20 rounded-xl overflow-hidden flex flex-col">
            {selectedMessage ? (
              <>
                {/* Thread Header */}
                <div className="p-4 border-b border-[#00AEEF]/10">
                  <h2 className="text-white font-bold">{selectedMessage.subject}</h2>
                  <p className="text-[#94A3B8] text-sm">with {selectedMessage.teacher_name}</p>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4 max-h-[400px]">
                  {selectedMessage.messages?.map((msg) => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.sender === 'student' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div className={`max-w-[80%] ${
                        msg.sender === 'student' 
                          ? 'bg-[#00AEEF]/20 border border-[#00AEEF]/30' 
                          : 'bg-[#060F22] border border-[#00AEEF]/10'
                      } rounded-xl p-4`}>
                        <div className="flex items-center gap-2 mb-2">
                          <User className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-white text-sm font-medium">{msg.sender_name}</span>
                          <span className="text-[#94A3B8] text-xs">{formatTime(msg.sent_at)}</span>
                        </div>
                        <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                        {msg.sender === 'student' && msg.read && (
                          <div className="flex items-center justify-end mt-2">
                            <CheckCheck className="w-4 h-4 text-[#00FFE0]" />
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Reply Form */}
                <form onSubmit={sendReply} className="p-4 border-t border-[#00AEEF]/10">
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Type your reply..."
                      data-testid="message-reply-input"
                      className="flex-1 bg-[#060F22] border border-[#00AEEF]/30 text-white rounded-xl px-4 py-2.5 focus:border-[#00AEEF]"
                    />
                    <button
                      type="submit"
                      disabled={!replyText.trim()}
                      data-testid="send-reply-btn"
                      className="bg-[#00AEEF] text-white px-4 py-2.5 rounded-xl hover:bg-[#00AEEF]/80 transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      <Send className="w-4 h-4" />
                      Send
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="w-16 h-16 text-[#94A3B8] mx-auto mb-4" />
                  <p className="text-[#94A3B8]">Select a conversation to view</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentMessages;
